// Svelte islands over server-rendered pages: any element with
// data-svelte-component mounts the named component with data-props as its
// props. Mounted on every Turbo visit; unmounted before Turbo caches the page
// so restored snapshots hold an empty root and remount cleanly.
import { mount, unmount } from "svelte"
import AttachmentsDropzone from "./components/AttachmentsDropzone.svelte"
import CustomFieldForm from "./components/CustomFieldForm.svelte"
import EventCards from "./components/EventCards.svelte"
import Lightbox from "./components/Lightbox.svelte"
import OccurrencesTable from "./components/OccurrencesTable.svelte"
import PhotoDropzone from "./components/PhotoDropzone.svelte"
import SearchTable from "./components/SearchTable.svelte"
import ThemeSwitcher from "./components/ThemeSwitcher.svelte"
import Toasts from "./components/Toasts.svelte"

const registry = {
  AttachmentsDropzone,
  CustomFieldForm,
  EventCards,
  Lightbox,
  OccurrencesTable,
  PhotoDropzone,
  SearchTable,
  ThemeSwitcher,
  Toasts,
}
const active = new Map()

// A mount point may ship a server-rendered stand-in for what its component
// draws, so the page paints at the right height before the bundle has parsed
// (see home/_card_grid_skeleton). Svelte appends its own nodes after it, so
// hiding it is enough -- and hiding rather than removing keeps it in the
// snapshot turbo:before-cache takes, so a restored page paints at that height
// too instead of collapsing until the island remounts.
function skeletonIn(el) {
  return el.querySelector(":scope > [data-island-skeleton]")
}

function mountIslands() {
  for (const el of document.querySelectorAll("[data-svelte-component]")) {
    if (active.has(el)) continue
    const Component = registry[el.dataset.svelteComponent]
    if (!Component) continue
    const props = el.dataset.props ? JSON.parse(el.dataset.props) : {}
    const skeleton = skeletonIn(el)
    if (skeleton) skeleton.hidden = true
    active.set(el, mount(Component, { target: el, props }))
  }
}

function unmountIslands() {
  for (const [el, instance] of active) {
    unmount(instance)
    const skeleton = skeletonIn(el)
    if (skeleton) skeleton.hidden = false
  }
  active.clear()
}

// Turbo 8 morphs the DOM on a same-URL refresh (e.g. a form that redirects
// back to the page it posted from): turbo:load never fires and the morph
// overwrites each island's mounted content with the server's empty root, so
// the registry still thinks dead, empty islands are alive. Tear them all down
// and remount from the fresh DOM.
function remountIslands() {
  unmountIslands()
  mountIslands()
}

document.addEventListener("turbo:load", mountIslands)
document.addEventListener("turbo:morph", remountIslands)
document.addEventListener("turbo:before-cache", unmountIslands)
